/**
 * Logger Utility
 *
 * Provides environment-aware logging that suppresses debug output in production.
 * In production, only warnings and errors are logged.
 * In development, all log levels are enabled.
 */

const isProduction =
    import.meta.env.VITE_ENVIRONMENT === 'prod' || ['production', 'release'].includes(import.meta.env.MODE);

/**
 * Logger with environment-aware logging levels
 */
export const logger = {
    /**
     * Debug level - only logs in development
     * Use for detailed debugging information
     */
    debug(...args) {
        if (!isProduction) {
            console.log('[DEBUG]', ...args);
        }
    },

    /**
     * Info level - only logs in development
     * Use for general information
     */
    info(...args) {
        if (!isProduction) {
            console.log('[INFO]', ...args);
        }
    },

    /**
     * Warning level - logs in all environments
     * Use for non-critical issues that should be investigated
     */
    warn(...args) {
        console.warn('[WARN]', ...args);
    },

    /**
     * Error level - logs in all environments
     * Use for errors that need attention
     * Note: Avoid logging sensitive data in error messages
     */
    error(...args) {
        // Filter out potentially sensitive data
        const sanitizedArgs = args.map(arg => {
            if (typeof arg === 'string') {
                // Remove potential tokens or sensitive data patterns
                return arg
                    .replace(/Bearer\s+[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/gi, 'Bearer [REDACTED]')
                    .replace(/token[=:]\s*["']?[A-Za-z0-9\-_]+["']?/gi, 'token=[REDACTED]')
                    .replace(/sig=[A-Za-z0-9%\-_]+/gi, 'sig=[REDACTED]');
            }
            return arg;
        });
        console.error('[ERROR]', ...sanitizedArgs);
    },

    /**
     * Log API request/response info - only in development
     * Automatically redacts sensitive headers
     */
    api(method, url, status) {
        if (!isProduction) {
            // Redact query parameters that might contain sensitive data
            const sanitizedUrl = url
                .replace(/sig=[^&]+/gi, 'sig=[REDACTED]')
                .replace(/token=[^&]+/gi, 'token=[REDACTED]')
                .replace(/api-key=[^&]+/gi, 'api-key=[REDACTED]');
            console.log(`[API] ${method} ${sanitizedUrl} - ${status}`);
        }
    }
};

export default logger;
